<template>
  <v-card class="secondary" height="100%">
    <v-form ref="form">
      <v-card-title class="font-weight-medium secondary--text primary">
        {{ edit ? `Edit Course Fee` : "Add Course Fee" }}
        <v-spacer></v-spacer>
        <v-btn small icon @click="$emit('closeDialog')">
          <v-icon small color="secondary">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-5">
        <v-row>
          <v-col cols="3">
            <v-autocomplete
              v-model="form.partner_id"
              :items="partners"
              item-text="name"
              item-value="id"
              label="Partner*"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
              @change="partnerSettings"
            />
          </v-col>
          <v-col cols="3"
            ><v-text-field
              v-model.trim="form.advance_fee"
              label="Tuition Fee Advance"
              outlined
              dense
              suffix="₹"
              hide-details="auto"
              :rules="[rules.zeroRequired, rules.number]"
              :disabled="partnerAllowed"
          /></v-col>
          <v-col cols="3">
            <v-text-field
              v-model.trim="form.application_fee"
              label="Application Fee"
              outlined
              dense
              suffix="₹"
              hide-details="auto"
              :rules="[rules.zeroRequired, rules.number]"
              :disabled="partnerAllowed"
            />
          </v-col>
          <v-col cols="3">
            <v-text-field
              v-model.trim="form.admission_fee"
              label="Admission Fee"
              outlined
              dense
              suffix="₹"
              hide-details="auto"
              :rules="[rules.zeroRequired, rules.number]"
              :disabled="partnerAllowed"
            />
          </v-col>
          <v-col cols="3">
            <v-text-field
              v-model.trim="form.course_fee"
              label="Course Fee"
              outlined
              dense
              suffix="₹"
              hide-details="auto"
              :rules="[rules.zeroRequired, rules.courseFeeGreater(form)]"
            />
          </v-col>
          <v-col cols="3">
            <v-text-field
              v-model.trim="form.exam_fee"
              label="Exam Fee"
              outlined
              dense
              suffix="₹"
              hide-details="auto"
              :rules="[rules.zeroRequired, rules.number]"
            />
          </v-col>
          <v-col cols="3">
            <v-text-field
              v-model.trim="form.arrear_fee"
              label="Arrear Fee"
              outlined
              dense
              suffix="₹"
              hide-details="auto"
              :rules="[rules.zeroRequired, rules.number]"
            />
          </v-col>
          <v-col cols="12">
            <v-row>
              <v-col cols="4">
                <v-text-field
                  v-model="this.form.term_fee"
                  label="Term Fee"
                  outlined
                  dense
                  suffix="₹"
                  hide-details="auto"
                  readonly
                />
              </v-col>
              <v-col cols="4" class="d-flex pr-0 align-center">
                <v-checkbox
                  v-model="form.term_fee_loan_allowed"
                  label="Loan Allowed"
                  hide-details="auto"
                  class="mt-0"
                  @change="onLoanAllowedChange($event, 'term_')"
                  :disabled="partnerAllowed"
                ></v-checkbox>
                <v-checkbox
                  v-model="form.term_fee_installments_allowed"
                  label="Installments Allowed"
                  hide-details="auto"
                  class="mt-0 pl-2"
                  @change="onInstallmentsAllowedChange($event, 'term_')"
                  :disabled="partnerAllowed"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" md="12" v-if="program_duration != 'Y'">
            <v-row>
              <v-col cols="4">
                <v-text-field
                  v-model.trim="form.annual_upfront"
                  label="Annual Upfront*"
                  outlined
                  dense
                  suffix="₹"
                  hide-details="auto"
                  :rules="[
                    rules.number,
                    rules.full_annual_upfront(form, 'Annual Upfront Fee'),
                  ]"
                />
              </v-col>
              <v-col cols="4" class="d-flex pr-0 align-center">
                <v-checkbox
                  v-model="form.annual_upfront_loan_allowed"
                  label="Loan Allowed"
                  hide-details="auto"
                  class="mt-0"
                  @change="onLoanAllowedChange($event, 'annual_upfront')"
                  :disabled="partnerAllowed"
                ></v-checkbox>
                <v-checkbox
                  v-model="form.annual_upfront_installments_allowed"
                  label="Installments Allowed"
                  hide-details="auto"
                  class="mt-0 pl-2"
                  @change="
                    onInstallmentsAllowedChange($event, 'annual_upfront')
                  "
                  :disabled="partnerAllowed"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12">
            <v-row>
              <v-col cols="4">
                <v-text-field
                  v-model.trim="form.full_upfront"
                  label="Full Upfront*"
                  outlined
                  dense
                  suffix="₹"
                  hide-details="auto"
                  :rules="[
                    rules.number,
                    rules.full_annual_upfront(form, 'Full Upfront Fee'),
                  ]"
                />
              </v-col>
              <v-col cols="4" class="d-flex pr-0 align-center">
                <v-checkbox
                  v-model="form.full_upfront_loan_allowed"
                  label="Loan Allowed"
                  hide-details="auto"
                  class="mt-0"
                  @change="onLoanAllowedChange($event, 'full_upfront')"
                  :disabled="partnerAllowed"
                ></v-checkbox>
                <v-checkbox
                  v-model="form.full_upfront_installments_allowed"
                  label="Installments Allowed"
                  hide-details="auto"
                  class="mt-0 pl-2"
                  @change="onInstallmentsAllowedChange($event, 'full_upfront')"
                  :disabled="partnerAllowed"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="pt-0 mb-3 mx-2">
        <v-spacer></v-spacer>
        <v-btn class="primary" @click="save">
          {{ edit ? "Update" : "Save" }}
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import { rules } from "@/helpers/rules";

export default {
  props: {
    batchCourseFee: {
      type: Object,
      default: () => ({}),
    },
    programs: {
      type: Array,
      default: () => [],
    },
    partners: {
      type: Array,
      default: () => [],
    },
    edit: {
      type: Boolean,
      default: false,
    },
    program_id: {
      type: Number,
      default: "",
    },
  },
  data() {
    return {
      rules,
      form: {},
      program_duration: "",
      partnerAllowed: false,
    };
  },
  computed: {
    currentProgram() {
      return this.programs.find((p) => p.id === this.program_id) || {};
    },
  },
  watch: {
    "form.course_fee"(val) {
      this.form.term_fee = (val / this.currentProgram.duration).toFixed(2);
    },
  },
  created() {
    this.init();
    this.program_duration = this.currentProgram.duration_type || "";
  },
  methods: {
    init() {
      if (this.edit) {
        this.form = { ...this.batchCourseFee };
      }
    },
    partnerSettings() {
      const partner = this.partners.find(
        (item) => this.form.partner_id == item.id
      );
      if (partner.partner_payment_required) {
        this.form = {
          ...this.form,
          admission_fee: 0,
          advance_fee: 0,
          application_fee: 0,
          term_fee_loan_allowed: false,
          term_fee_installments_allowed: false,
          annual_upfront_loan_allowed: false,
          annual_upfront_installments_allowed: false,
          full_upfront_loan_allowed: false,
          full_upfront_installments_allowed: false,
        };
        this.partnerAllowed = true;
      } else {
        this.partnerAllowed = false;
      }
    },
    save() {
      this.form = {
        partner_id: this.form.partner_id,
        course_fee: this.form.course_fee || 0,
        admission_fee: this.form.admission_fee || 0,
        advance_fee: this.form.advance_fee || 0,
        arrear_fee: this.form.arrear_fee || 0,
        exam_fee: this.form.exam_fee || 0,
        term_fee: this.form.term_fee || 0,
        term_fee_loan_allowed: this.form.term_fee_loan_allowed,
        term_fee_installments_allowed: this.form.term_fee_installments_allowed,
        annual_upfront: this.form.annual_upfront || 0,
        annual_upfront_loan_allowed: this.form.annual_upfront_loan_allowed,
        annual_upfront_installments_allowed:
          this.form.annual_upfront_installments_allowed,
        full_upfront: this.form.full_upfront || 0,
        full_upfront_loan_allowed: this.form.full_upfront_loan_allowed,
        full_upfront_installments_allowed:
          this.form.full_upfront_installments_allowed,
        term_fee_no_of_installments: this.form.term_fee_no_of_installments,
        annual_upfront_no_of_installments:
          this.form.annual_upfront_no_of_installments,
        full_upfront_no_of_installments:
          this.form.full_upfront_no_of_installments,
        application_fee: this.form.application_fee || 0,
        id: this.form.id,
      };
      if (!this.$refs.form.validate()) return;
      this.edit
        ? this.$emit("update", this.form)
        : this.$emit("create", { ...this.form, active: true });
    },
    onLoanAllowedChange(val, keyToUpdate) {
      let installments_allowed = keyToUpdate + "installments_allowed";
      if (val) {
        this.form[installments_allowed] = false;
      }
    },
    onInstallmentsAllowedChange(val, keyToUpdate) {
      let loan_allowed = keyToUpdate + "loan_allowed";
      if (val) {
        this.form[loan_allowed] = false;
      }
    },
  },
};
</script>
